import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, Users, FileText, Mail, Lock, Send, ArrowLeft, CheckCircle2, AlertTriangle, Download, CalendarRange } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtMoney } from "@/lib/format";
import { PageHeader, EmptyState } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EMPTY_EMP = { name: "", position: "", email: "", employee_no: "", inss_no: "", base_salary: "", allowances: "0", annual_leave_days: "12", bank_name: "", bank_account: "", start_date: "", notes: "" };
const monthLabel = (ym) => new Date(`${ym}-01T00:00:00`).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
const onErr = (e) => toast.error(e?.response?.data?.detail || "Request failed");

/** Admin-only payroll: employees + monthly payslip runs (INSS / WHT), PDF + email. */
export default function Payroll() {
  return (
    <div data-testid="payroll-page">
      <PageHeader overline="HR · Admin only" title="Payroll & Payslips"
        subtitle="Employees, monthly payroll runs with INSS (Segurança Social) and withholding tax, payslip PDFs emailed to each employee." />
      <Tabs defaultValue="runs">
        <TabsList className="mb-4">
          <TabsTrigger value="runs" data-testid="payroll-tab-runs">Payroll runs</TabsTrigger>
          <TabsTrigger value="employees" data-testid="payroll-tab-employees">Employees</TabsTrigger>
        </TabsList>
        <TabsContent value="runs"><RunsTab /></TabsContent>
        <TabsContent value="employees"><EmployeesTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function EmployeesTab() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const { data: rows = [], isLoading } = useQuery({ queryKey: ["employees"], queryFn: api.listEmployees });
  const refresh = () => qc.invalidateQueries({ queryKey: ["employees"] });
  const yearPdf = (e) => api.openEmployeeYearSummaryPdf(e.id, year).catch(async (err) => {
    let detail = "Could not generate the year summary";
    try { detail = JSON.parse(await err?.response?.data?.text())?.detail || detail; } catch { /* keep default */ }
    toast.error(detail);
  });
  const emailOne = useMutation({ mutationFn: (id) => api.emailEmployeeYearSummary(id, Number(year)), onSuccess: (r) => toast.success(`${r.year} summary emailed to ${r.to}`), onError: onErr });
  const emailAll = useMutation({
    mutationFn: () => api.emailAllYearSummaries(Number(year)),
    onSuccess: (r) => {
      toast.success(`${r.sent_count} year summar${r.sent_count === 1 ? "y" : "ies"} emailed for ${r.year}${r.skipped_count ? ` · ${r.skipped_count} skipped` : ""}`);
      if (r.failed_count) toast.error(`${r.failed_count} failed: ${r.failed.map((f) => `${f.name} (${f.detail})`).join("; ")}`);
    },
    onError: onErr,
  });
  const saveMut = useMutation({
    mutationFn: (f) => {
      const body = { ...f, base_salary: Number(f.base_salary), allowances: Number(f.allowances || 0), annual_leave_days: Number(f.annual_leave_days || 12) };
      return f.id ? api.updateEmployee(f.id, body) : api.createEmployee(body);
    },
    onSuccess: () => { toast.success("Employee saved"); setEditing(null); refresh(); }, onError: onErr,
  });
  const activeMut = useMutation({ mutationFn: ({ id, active }) => api.updateEmployee(id, { active }), onSuccess: refresh, onError: onErr });
  const delMut = useMutation({ mutationFn: (id) => api.deleteEmployee(id), onSuccess: () => { toast.success("Employee removed"); setConfirmDel(null); refresh(); }, onError: onErr });

  return (
    <div data-testid="employees-tab">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
        <div className="flex items-end gap-2">
          <div><Label className="overline">Summary year</Label>
            <Input data-testid="employee-summary-year" type="number" min="2000" max="2100" className="w-28 mono" value={year} onChange={(e) => setYear(e.target.value)} /></div>
          <p className="text-xs text-muted-foreground pb-2 max-w-md">Per row: <CalendarRange className="inline size-3" /> opens the annual earnings &amp; INSS summary PDF, <Send className="inline size-3" /> emails it to that employee (finalized payroll months only).</p>
          <Button variant="outline" data-testid="employee-year-summary-email-all" disabled={emailAll.isPending || !/^\d{4}$/.test(year)} onClick={() => emailAll.mutate()}><Mail className="size-4 mr-1.5" /> {emailAll.isPending ? "Sending…" : `Email ${year} summaries to all`}</Button>
        </div>
        <Button data-testid="employee-add-btn" onClick={() => setEditing({ ...EMPTY_EMP })}><Plus className="size-4 mr-1.5" /> Add employee</Button>
      </div>
      {isLoading ? <p className="text-sm text-muted-foreground">Loading…</p> : rows.length === 0 ? (
        <div data-testid="employees-empty"><EmptyState title="No employees yet" hint="Add your staff to start running payroll." /></div>
      ) : (
        <div className="border border-border rounded-md bg-white overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="hover:bg-transparent">{["Employee", "Position", "INSS no.", "Base salary", "Allowances", "Active", ""].map((h) => <TableHead key={h} className="overline">{h}</TableHead>)}</TableRow></TableHeader>
            <TableBody>
              {rows.map((e) => (
                <TableRow key={e.id} data-testid={`employee-row-${e.id}`} className={e.active ? "" : "opacity-60"}>
                  <TableCell className="font-medium">{e.name}<div className="text-[10px] text-muted-foreground mono">{e.email || "no email"}{e.employee_no ? ` · ${e.employee_no}` : ""}</div></TableCell>
                  <TableCell className="text-sm">{e.position || "—"}</TableCell>
                  <TableCell className="mono text-xs">{e.inss_no || "—"}</TableCell>
                  <TableCell className="mono text-sm">{fmtMoney(e.base_salary)}</TableCell>
                  <TableCell className="mono text-sm">{fmtMoney(e.allowances)}</TableCell>
                  <TableCell><Switch data-testid={`employee-active-${e.id}`} checked={!!e.active} onCheckedChange={(v) => activeMut.mutate({ id: e.id, active: v })} /></TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button variant="ghost" size="icon" className="size-8" data-testid={`employee-year-summary-${e.id}`} title={`Annual earnings & INSS summary ${year}`} aria-label="Year summary PDF" onClick={() => yearPdf(e)}><CalendarRange className="size-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="size-8" data-testid={`employee-year-summary-email-${e.id}`} title={`Email ${year} summary`} aria-label="Email year summary" disabled={!e.email || emailOne.isPending} onClick={() => emailOne.mutate(e.id)}><Send className="size-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="size-8" data-testid={`employee-edit-${e.id}`} onClick={() => setEditing({ ...EMPTY_EMP, ...e })}><Pencil className="size-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="size-8 text-red-600" data-testid={`employee-delete-${e.id}`} onClick={() => setConfirmDel(e)}><Trash2 className="size-3.5" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent data-testid="employee-dialog" className="max-w-lg">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit employee" : "Add employee"}</DialogTitle></DialogHeader>
          {editing && (
            <form className="grid grid-cols-1 sm:grid-cols-2 gap-3" onSubmit={(e) => { e.preventDefault(); saveMut.mutate(editing); }}>
              {[["name", "Full name *", "text"], ["position", "Position", "text"], ["email", "Email (payslips)", "email"], ["employee_no", "Employee no.", "text"],
                ["inss_no", "INSS / Segurança Social no.", "text"], ["base_salary", "Monthly base salary (USD) *", "number"], ["allowances", "Monthly allowances (USD)", "number"], ["annual_leave_days", "Annual leave days / year", "number"],
                ["bank_name", "Bank", "text"], ["bank_account", "Bank account", "text"], ["start_date", "Start date", "date"]].map(([k, l, type]) => (
                <div key={k} className={k === "name" ? "sm:col-span-2" : ""}>
                  <Label className="overline">{l}</Label>
                  <Input data-testid={`employee-field-${k}`} type={type} step={type === "number" ? "0.01" : undefined} min={type === "number" ? "0" : undefined}
                         value={editing[k] ?? ""} onChange={(e) => setEditing((s) => ({ ...s, [k]: e.target.value }))} required={l.endsWith("*")} />
                </div>
              ))}
              <DialogFooter className="sm:col-span-2">
                <Button type="button" variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                <Button type="submit" data-testid="employee-save" disabled={saveMut.isPending}>Save</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={!!confirmDel} onOpenChange={(v) => !v && setConfirmDel(null)}>
        <DialogContent data-testid="employee-delete-dialog">
          <DialogHeader><DialogTitle>Remove {confirmDel?.name}?</DialogTitle><DialogDescription>Past payslips are kept. Prefer deactivating if they may return.</DialogDescription></DialogHeader>
          <DialogFooter><Button variant="outline" onClick={() => setConfirmDel(null)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700" data-testid="employee-delete-confirm" onClick={() => delMut.mutate(confirmDel.id)}>Remove</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RunsTab() {
  const qc = useQueryClient();
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [runId, setRunId] = useState(null);
  const { data: runs = [], isLoading } = useQuery({ queryKey: ["payroll-runs"], queryFn: api.listPayrollRuns });
  const createMut = useMutation({
    mutationFn: () => api.createPayrollRun(month),
    onSuccess: (r) => { toast.success(`Payroll for ${monthLabel(r.month)} generated — ${r.totals.employees} payslips`); qc.invalidateQueries({ queryKey: ["payroll-runs"] }); setRunId(r.id); },
    onError: onErr,
  });
  if (runId) return <RunDetail runId={runId} onBack={() => { setRunId(null); qc.invalidateQueries({ queryKey: ["payroll-runs"] }); }} />;
  const finalizedYears = [...new Set(runs.filter((r) => r.status === "finalized").map((r) => r.month.slice(0, 4)))].sort().reverse();
  return (
    <div data-testid="runs-tab">
      <div className="flex flex-wrap items-end gap-3 mb-4 border border-border rounded-md bg-white p-4">
        <div><Label className="overline">Pay month</Label><Input data-testid="run-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44" /></div>
        <Button data-testid="run-create-btn" disabled={createMut.isPending || !month} onClick={() => createMut.mutate()}><Plus className="size-4 mr-1.5" /> Generate payslips</Button>
        <p className="text-xs text-muted-foreground">Creates a draft payslip for every active employee using the INSS / tax rates from Settings.</p>
        <YearArchive key={finalizedYears.join(",")} years={finalizedYears} />
      </div>
      {isLoading ? <p className="text-sm text-muted-foreground">Loading…</p> : runs.length === 0 ? (
        <div data-testid="runs-empty"><EmptyState title="No payroll runs yet" hint="Pick a month and generate payslips." /></div>
      ) : (
        <div className="border border-border rounded-md bg-white overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="hover:bg-transparent">{["Month", "Employees", "Gross", "INSS (emp + er)", "Tax", "Net pay", "Emailed · ✓received", "Status"].map((h) => <TableHead key={h} className="overline">{h}</TableHead>)}</TableRow></TableHeader>
            <TableBody>
              {runs.map((r) => (
                <TableRow key={r.id} data-testid={`run-row-${r.id}`} className="cursor-pointer" onClick={() => setRunId(r.id)}>
                  <TableCell className="font-medium">{monthLabel(r.month)}</TableCell>
                  <TableCell className="mono text-sm">{r.totals.employees}</TableCell>
                  <TableCell className="mono text-sm">{fmtMoney(r.totals.gross)}</TableCell>
                  <TableCell className="mono text-sm">{fmtMoney(r.totals.inss_employee)} + {fmtMoney(r.totals.inss_employer)}</TableCell>
                  <TableCell className="mono text-sm">{fmtMoney(r.totals.withholding_tax)}</TableCell>
                  <TableCell className="mono text-sm font-semibold">{fmtMoney(r.totals.net_pay)}</TableCell>
                  <TableCell className="mono text-xs">{r.totals.emailed}/{r.totals.employees}<span className="text-muted-foreground" title="Confirmed received by employees"> · ✓{r.totals.acknowledged ?? 0}</span></TableCell>
                  <TableCell><span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ring-1 ring-inset ${r.status === "draft" ? "bg-amber-50 text-amber-800 ring-amber-200" : "bg-emerald-50 text-emerald-700 ring-emerald-200"}`}>{r.status}</span></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function YearArchive({ years }) {
  const [year, setYear] = useState(years[0] || String(new Date().getFullYear()));
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try { await api.downloadPayrollArchive(year); toast.success(`Payroll archive ${year} downloaded`); }
    catch (err) {
      let detail = "Could not build the archive";
      try { detail = JSON.parse(await err?.response?.data?.text())?.detail || detail; } catch { /* keep default */ }
      toast.error(detail);
    } finally { setBusy(false); }
  };
  return (
    <div className="ml-auto flex items-end gap-2 border-l border-border pl-4" data-testid="payroll-archive-box">
      <div><Label className="overline">Archive year</Label>
        <Input data-testid="payroll-archive-year" type="number" min="2000" max="2100" className="w-28 mono" value={year} onChange={(e) => setYear(e.target.value)} /></div>
      <Button variant="outline" data-testid="payroll-archive-btn" disabled={busy || !/^\d{4}$/.test(year)} onClick={download} title="ZIP: payslips + year summary per employee, INSS statements, bank CSVs, summary.csv">
        <Download className="size-4 mr-1.5" /> {busy ? "Building…" : `Download ${year} archive (ZIP)`}
      </Button>
    </div>
  );
}

function RunDetail({ runId, onBack }) {
  const qc = useQueryClient();
  const { data: run } = useQuery({ queryKey: ["payroll-run", runId], queryFn: () => api.getPayrollRun(runId) });
  const refresh = () => qc.invalidateQueries({ queryKey: ["payroll-run", runId] });
  const editMut = useMutation({ mutationFn: ({ slipId, body }) => api.updatePayslip(runId, slipId, body), onSuccess: refresh, onError: onErr });
  const finalizeMut = useMutation({ mutationFn: () => api.finalizePayrollRun(runId), onSuccess: () => { toast.success("Payroll finalized — payslips locked"); refresh(); }, onError: onErr });
  const emailAll = useMutation({
    mutationFn: () => api.emailPayrollRun(runId, {}),
    onSuccess: (r) => { toast.success(`${r.sent_count} payslip${r.sent_count === 1 ? "" : "s"} emailed`); if (r.failed_count) toast.error(`${r.failed_count} failed: ${r.failed.map((f) => `${f.name} (${f.error})`).join("; ")}`); refresh(); },
    onError: onErr,
  });
  const emailOne = useMutation({ mutationFn: (slipId) => api.emailPayslip(runId, slipId), onSuccess: (r) => { toast.success(`Payslip emailed to ${r.to}`); refresh(); }, onError: onErr });
  const delMut = useMutation({ mutationFn: () => api.deletePayrollRun(runId), onSuccess: () => { toast.success("Draft run deleted"); onBack(); }, onError: onErr });
  if (!run) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const draft = run.status === "draft";
  const t = run.totals;
  const NumCell = ({ slip, field }) => draft ? (
    <Input data-testid={`slip-${field}-${slip.id}`} type="number" min="0" step="0.01" className="h-8 w-24 mono text-xs" defaultValue={slip[field]}
           onBlur={(e) => { const v = Number(e.target.value || 0); if (v !== slip[field]) editMut.mutate({ slipId: slip.id, body: { [field]: v } }); }} />
  ) : <span className="mono text-xs">{fmtMoney(slip[field])}</span>;

  return (
    <div data-testid="run-detail">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Button variant="ghost" size="sm" data-testid="run-back" onClick={onBack}><ArrowLeft className="size-3.5 mr-1" /> All runs</Button>
        <h3 className="font-display font-semibold text-lg">{monthLabel(run.month)}</h3>
        <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ring-1 ring-inset ${draft ? "bg-amber-50 text-amber-800 ring-amber-200" : "bg-emerald-50 text-emerald-700 ring-emerald-200"}`} data-testid="run-status">{run.status}</span>
        <span className="text-xs text-muted-foreground">INSS {run.rates.inss_employee_pct}% / {run.rates.inss_employer_pct}% · Tax {run.rates.wht_pct}% above {fmtMoney(run.rates.wht_threshold)}</span>
        <div className="ml-auto flex flex-wrap gap-2">
          <Button variant="outline" data-testid="run-inss-pdf" onClick={() => api.openInssReportPdf(runId)}><FileText className="size-4 mr-1.5" /> INSS statement</Button>
          <Button variant="outline" data-testid="run-bank-csv" onClick={() => api.downloadPayrollBankCsv(runId, run.month)}><Download className="size-4 mr-1.5" /> Bank transfer CSV</Button>
          <Button variant="outline" data-testid="run-email-all" disabled={emailAll.isPending} onClick={() => emailAll.mutate()}><Mail className="size-4 mr-1.5" /> {emailAll.isPending ? "Sending…" : "Email all payslips"}</Button>
          {draft && <Button data-testid="run-finalize" className="bg-slate-900 hover:bg-slate-800" disabled={finalizeMut.isPending} onClick={() => finalizeMut.mutate()}><Lock className="size-4 mr-1.5" /> Finalize</Button>}
          {draft && <Button variant="ghost" className="text-red-600" data-testid="run-delete" onClick={() => delMut.mutate()}><Trash2 className="size-4" /></Button>}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-4 text-xs">
        {[["Gross", t.gross], ["INSS employee", t.inss_employee], ["INSS employer", t.inss_employer], ["Withholding tax", t.withholding_tax], ["Net pay", t.net_pay], ["Total employer cost", t.employer_cost]].map(([l, v]) => (
          <div key={l} className="rounded-md border border-border bg-slate-50 p-2" data-testid={`run-total-${l.toLowerCase().replace(/ /g, "-")}`}><p className="overline">{l}</p><p className="mono font-semibold mt-0.5">{fmtMoney(v)}</p></div>
        ))}
      </div>

      <div className="border border-border rounded-md bg-white overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="hover:bg-transparent">{["Employee", "Base", "Allow.", "Overtime", "Bonus", "Gross", "INSS emp", "INSS er", "Tax", "Other ded.", "Net pay", "Email", ""].map((h) => <TableHead key={h} className="overline">{h}</TableHead>)}</TableRow></TableHeader>
          <TableBody>
            {run.slips.map((s) => (
              <TableRow key={s.id} data-testid={`slip-row-${s.id}`}>
                <TableCell className="font-medium text-sm">{s.name}<div className="text-[10px] text-muted-foreground">{s.position}{s.inss_no ? ` · INSS ${s.inss_no}` : ""}</div></TableCell>
                <TableCell className="mono text-xs">{fmtMoney(s.base_salary)}</TableCell>
                <TableCell className="mono text-xs">{fmtMoney(s.allowances)}</TableCell>
                <TableCell><NumCell slip={s} field="overtime" /></TableCell>
                <TableCell><NumCell slip={s} field="bonus" /></TableCell>
                <TableCell className="mono text-xs font-semibold">{fmtMoney(s.gross)}</TableCell>
                <TableCell className="mono text-xs text-red-700">{fmtMoney(s.inss_employee)}</TableCell>
                <TableCell className="mono text-xs text-muted-foreground">{fmtMoney(s.inss_employer)}</TableCell>
                <TableCell className="mono text-xs text-red-700">{fmtMoney(s.withholding_tax)}</TableCell>
                <TableCell><NumCell slip={s} field="other_deductions" /></TableCell>
                <TableCell className="mono text-sm font-bold text-emerald-700" data-testid={`slip-net-${s.id}`}>{fmtMoney(s.net_pay)}</TableCell>
                <TableCell className="text-xs">
                  {s.emailed_at ? <span className="inline-flex items-center gap-1 text-emerald-700" data-testid={`slip-emailed-${s.id}`}><CheckCircle2 className="size-3" /> sent</span>
                    : s.email_error ? <span className="inline-flex items-center gap-1 text-red-600" title={s.email_error}><AlertTriangle className="size-3" /> failed</span>
                    : <span className="text-muted-foreground">{s.email ? "—" : "no email"}</span>}
                  {s.acknowledged_at ? <div className="inline-flex items-center gap-1 text-emerald-700 mt-0.5" data-testid={`slip-acked-${s.id}`} title={s.acknowledged_at}><CheckCircle2 className="size-3" /> received</div>
                    : <div className="text-[10px] text-muted-foreground" data-testid={`slip-not-acked-${s.id}`}>not yet confirmed</div>}
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button variant="ghost" size="icon" className="size-8" data-testid={`slip-pdf-${s.id}`} onClick={() => api.openPayslipPdf(runId, s.id)} aria-label="PDF"><FileText className="size-3.5" /></Button>
                  <Button variant="ghost" size="icon" className="size-8" data-testid={`slip-email-${s.id}`} disabled={!s.email || emailOne.isPending} onClick={() => emailOne.mutate(s.id)} aria-label="Email payslip"><Send className="size-3.5" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {draft && <p className="text-[11px] text-muted-foreground mt-2 flex items-center gap-1"><Users className="size-3" /> Edit overtime, bonus and other deductions inline (saved on blur). Finalize to lock the payslips.</p>}
    </div>
  );
}
